import React from 'react';

import { Link } from 'react-router-dom';

import { css } from '@emotion/css';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { ColumnType } from 'antd/lib/table';

import { AsyncTable } from 'components/AsyncTable';
import { useSearchIndexTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { LinkButton } from 'components/Button/LinkButton';
import { ButtonLink, SalesCustomerLink } from 'components/Links';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { filterCostAndGM } from 'utils/filterCostAndGM';
import { formatDate, formatUSD } from 'utils/formatting';
import { isAdmin, shouldUseCoreApiForSearch } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';

import { SearchIndexSalesOrder } from 'types/search-collections';

export const OpenOrdersTable = () => {
  const { activeTenant, activeErpRole } = useGlobalApp();

  const columns: ColumnType<SearchIndexSalesOrder>[] = [
    {
      title: 'Order No',
      dataIndex: 'order_no',
      render: (orderNo: string) => <Link to={routes.orders.orderDetails(orderNo)}>{orderNo} </Link>,
    },
    {
      title: `Order Date`,
      dataIndex: 'order_date',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Customer ID',
      dataIndex: 'customer_id',
      render: (customerId: string) => <SalesCustomerLink customerId={customerId}>{customerId}</SalesCustomerLink>,
    },
    { title: 'Customer', dataIndex: 'customer_name' },
    {
      title: 'Open Amount',
      dataIndex: 'total_open_value',
      render: (amt: number) => formatUSD(amt, true),
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_value',
      render: (amt: number) => formatUSD(amt, true),
    },
    {
      title: 'GM',
      dataIndex: 'gross_margin',
      render: (amt: number) => formatUSD(amt, true),
    },
    {
      title: 'Line Count',
      dataIndex: 'item_count',
    },
    {
      dataIndex: 'order_no',
      render: (orderNo: string) => ButtonLink(routes.orders.orderDetails(orderNo)),
    },
  ].filter(filterCostAndGM);

  let facetValueFilters: Record<string, string[]> = {
    order_type: ['order'],
    status: ['Open'],
    ...(!isAdmin(activeErpRole.foreignId, activeErpRole.name) && { salesrep_id: activeErpRole?.foreignIds }),
  };

  if (shouldUseCoreApiForSearch(activeTenant)) {
    // DPPA tenants use api, which only returns orders and doesn't support sales rep filters
    facetValueFilters = {
      status: ['open'],
    };
  }

  const sortBy: { field: string; order: 'desc' } = {
    field: 'order_date',
    order: 'desc',
  };

  return (
    <>
      <AsyncTable
        tableProps={useSearchIndexTableProps<SearchIndexSalesOrder>({
          indexName: SearchIndexName.SalesOrders,
          options: {
            filters: facetValueFilters,
            hitsPerPage: 15,
            sortBy,
          },
        })}
        columns={columns}
        rowKey="orderNo"
        pagination={false}
      />
      <div
        className={css`
          margin-top: 16px;
          justify-content: center;
          display: flex;
        `}
      >
        <LinkButton
          route={routes.orders.orderList({
            where: facetValueFilters,
            sortBy,
          })}
        >
          View All
        </LinkButton>
      </div>
    </>
  );
};
